import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { SharingBtn } from "@/components/Sharing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Copy, Clipboard, Eye, Download, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getSellerToUnityBalancesSimplified,
  getSellerToUnityDistributionDetails,
  distributionSellerForUnityBulkWithFiles,
} from "@/Api/distributions";
import { getShowContests } from "@/Api/contests";
import { storeDistributionsSellerToUnityWithFiles } from "@/Api/distributionsSellerToSeller";
import { FileUpload } from "../../components/FileUpload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { LoaderSpin, CreateLoaderInput } from "@/components/Loaders";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";

// Interfaces para o novo endpoint simplificado
interface SimplifiedUnityBalance {
  current_balance: string;
  held_balance: string;
}

interface SimplifiedUnity {
  id: number;
  name: string;
  contest_product_balance: SimplifiedUnityBalance;
}

interface SimplifiedProduct {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: string;
  };
  unity: SimplifiedUnity[];
  unities?: SimplifiedUnity[];
}

export const TableData = ({
  dataApi,
  pageActions,
}: {
  dataApi: any;
  pageActions: {
    update: () => Promise<void>;
    next?: () => Promise<void>;
    previous?: () => Promise<void>;
    lastPage?: () => Promise<void>;
  };
}) => {
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((contest: any) => (
            <TableRow key={contest.id}>
              <TableCell className="truncate max-w-80">{contest.name}</TableCell>
              <TableCell>
                <div
                  className="inline-block px-2 py-1 rounded text-white text-sm"
                  style={{ backgroundColor: contest.lottery?.color || "#gray" }}
                >
                  {contest.lottery?.name}
                </div>
              </TableCell>
              <TableCell>
                {contest.validation_date
                  ? Mask.transformMaskBirthdayInBr(contest.validation_date)
                  : "Não informado"}
              </TableCell>
              <TableCell className="text-center">
                <SharingBtn
                  labels={{ title: "Distribuir para Unidades" }}
                  params={{
                    func: getShowContests,
                    funcParams: contest.uuid,
                  }}
                  className="sm:max-w-[700px]"
                  render={(responseApi: any) => (
                    <InsideContestModal
                      contestData={responseApi}
                      pageActions={pageActions}
                    />
                  )}
                  loader={<CreateLoaderInput lines={4} />}
                />
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={4}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

function InsideContestModal({
  contestData,
  pageActions,
}: {
  contestData: any;
  pageActions: {
    update: () => Promise<void>;
  };
}) {
  const { user } = useAuth();
  const [simplifiedData, setSimplifiedData] = useState<SimplifiedProduct[]>([]);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [distributingProductId, setDistributingProductId] = useState<
    number | null
  >(null);
  const [quotaStates, setQuotaStates] = useState<{
    [key: string]: { unityId: number; quota: number }[];
  }>({});
  // Ref para armazenar as alterações do usuário (não é resetado pelo fetchPage)
  const userChangesRef = useRef<{
    [productId: string]: { unityId: number; quota: number }[];
  }>({});
  const [confirmDialogProduct, setConfirmDialogProduct] = useState<any>(null);
  const [confirmDialogFiles, setConfirmDialogFiles] = useState<File[]>([]);
  const [copiedDistribution, setCopiedDistribution] = useState<{
    unityId: number;
    quota: number;
    unityName: string;
  } | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] =
    useState<any>(null);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [showDistributeAllDialog, setShowDistributeAllDialog] = useState(false);
  // Snapshot dos dados a distribuir quando o dialog é aberto
  const [distributeAllSnapshot, setDistributeAllSnapshot] = useState<{
    products: SimplifiedProduct[];
    quotaStates: { [productId: string]: { unityId: number; quota: number }[] };
  } | null>(null);
  const [isDistributingAll, setIsDistributingAll] = useState(false);
  const [distributeAllFiles, setDistributeAllFiles] = useState<File[]>([]);
  const [currentUnity, setCurrentUnity] = useState<SimplifiedUnity | null>(null);
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  } | null>(null);
  // Filtro por produto
  const [productFilter, setProductFilter] = useState("");

  const sellerId = user?.seller?.id;

  const fetchPage = async (page: number, filter?: string) => {
    setIsModalLoading(true);
    try {
      const searchFilter = filter !== undefined ? filter : productFilter;
      const productName = searchFilter ? `B${searchFilter}` : undefined;
      const response = await getSellerToUnityBalancesSimplified(
        String(contestData.id),
        page,
        100,
        productName
      );

      if (response.data?.status) {
        const responseData = response.data.response;

        // Verificar se a resposta é paginada (tem .data) ou não paginada (array direto)
        const isPaginated = responseData && typeof responseData === 'object' && 'data' in responseData;
        const data = isPaginated
          ? (responseData.data as SimplifiedProduct[])
          : (Array.isArray(responseData) ? responseData as SimplifiedProduct[] : []);

        setSimplifiedData(data);

        if (isPaginated) {
          setPaginationInfo({
            current_page: responseData.current_page,
            per_page: responseData.per_page,
            total: responseData.total,
            last_page: responseData.last_page,
            from: responseData.from,
            to: responseData.to,
          });
        } else {
          setPaginationInfo({
            current_page: 1,
            per_page: data.length,
            total: data.length,
            last_page: 1,
            from: 1,
            to: data.length,
          });
        }

        // Extrair a unidade do vendedor da resposta da API (fonte de verdade)
        let unityFromApi: SimplifiedUnity | null = currentUnity;
        if (!currentUnity && data.length > 0 && data[0].unity && data[0].unity.length > 0) {
          unityFromApi = data[0].unity[0];
          setCurrentUnity(unityFromApi);
        }

        // Acumular quotaStates - manter alterações de outras páginas
        setQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            if (!newStates[productId] && unityFromApi) {
              newStates[productId] = [
                {
                  unityId: unityFromApi.id,
                  quota: 0,
                },
              ];
            }
          });
          return newStates;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados do concurso:", error);
      toast.error("Erro ao carregar distribuições");
    } finally {
      setIsModalLoading(false);
    }
  };

  // Buscar dados do concurso
  useEffect(() => {
    fetchPage(currentPage);
  }, [contestData.id]);

  // Efeito para buscar quando filtro muda (com debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPage(1, productFilter);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [productFilter]);

  function getCurrentSellerBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );

    const distributedQuantity = parseInt(
      String(
        product?.contest_product_classification.quota_total_distributed || "0"
      )
    );
    const totalQuantity = parseInt(
      String(
        product?.contest_product_classification.quota_total_quantity || "0"
      )
    );

    // Balance available é o distribuído menos o total conforme padrão SellerToSeller
    // Nunca negativo: inconsistência de dados não deve travar/confundir a distribuição
    return Math.max(0, totalQuantity - distributedQuantity);
  }

  function getAvailableBalance(productId: number): number {
    return getCurrentSellerBalance(productId);
  }

  function handleQuotaChange(
    productId: number,
    targetUnityId: number,
    quota: number
  ) {
    setQuotaStates((prev) => {
      const newStates = {
        ...prev,
        [productId]: prev[productId].map((item) =>
          item.unityId === targetUnityId ? { ...item, quota } : item
        ),
      };
      // Salvar no ref para persistir entre páginas
      userChangesRef.current[productId] = newStates[productId];
      return newStates;
    });
  }

  function handleCopyIndividualDistribution(
    targetUnityId: number,
    quota: number,
    unityName: string
  ) {
    setCopiedDistribution({
      unityId: targetUnityId,
      quota,
      unityName,
    });
    toast.success(`Distribuição de ${unityName} (${quota} cotas) copiada!`);
  }

  function handlePasteDistribution(productId: number, targetUnityId: number) {
    if (!copiedDistribution) {
      toast.error("Nenhuma distribuição copiada encontrada.");
      return;
    }

    setQuotaStates((prev) => {
      const newStates = {
        ...prev,
        [productId]: prev[productId].map((item) =>
          item.unityId === targetUnityId
            ? { ...item, quota: copiedDistribution.quota }
            : item
        ),
      };
      // Salvar no ref para persistir entre páginas
      userChangesRef.current[productId] = newStates[productId];
      return newStates;
    });

    toast.success(
      `${copiedDistribution.quota} cotas coladas para ${copiedDistribution.unityName}!`
    );
  }

  function getQuotaValidationInfo(
    productId: number,
    currentInputs: { unityId: number; quota: number }[]
  ) {
    const totalInputQuotas = currentInputs.reduce(
      (sum, item) => sum + item.quota,
      0
    );

    const availableBalance = getAvailableBalance(productId);
    const isOverLimit = totalInputQuotas > availableBalance;
    const remaining = availableBalance - totalInputQuotas;

    return {
      totalInputQuotas,
      availableBalance,
      isOverLimit,
      remaining,
      canProceed: !isOverLimit,
    };
  }

  async function handleSubmitDistribution(product: any, files: File[]) {
    if (!sellerId || !currentUnity) {
      toast.error("Dados do vendedor ou unidade não encontrados.");
      return;
    }

    if (files.length === 0) {
      toast.error("É obrigatório anexar um arquivo para distribuir.");
      return;
    }

    setDistributingProductId(product.id);
    const localQuotas = quotaStates[product.id] || [];

    // Filtrar apenas unidades com cotas > 0
    const modifiedUnities = localQuotas.filter((currentQuota) => {
      return currentQuota.quota > 0;
    });

    if (modifiedUnities.length === 0) {
      toast.warning("Nenhuma alteração foi detectada para enviar.");
      setDistributingProductId(null);
      return;
    }

    const validationInfo = getQuotaValidationInfo(product.id, localQuotas);

    if (validationInfo.isOverLimit) {
      const excess =
        validationInfo.totalInputQuotas - validationInfo.availableBalance;
      toast.error(
        `Não é possível prosseguir. Você está tentando distribuir ${validationInfo.totalInputQuotas} cotas, mas há apenas ${validationInfo.availableBalance} disponíveis. Excesso: ${excess} cotas.`
      );
      setDistributingProductId(null);
      return;
    }

    const formData = new FormData();
    formData.append("contest_id", String(contestData.id));
    formData.append("lottery_id", String(product.lottery_id || contestData.lottery_id));
    formData.append("from_seller_id", String(sellerId));
    formData.append("contest_product_classification_id", String(product.id));

    modifiedUnities.forEach((unity, index) => {
      formData.append(`unities[${index}][id]`, String(unity.unityId));
      formData.append(`unities[${index}][quota_quantity]`, String(unity.quota));
    });

    files.forEach((file) => {
      formData.append("files[]", file);
    });

    try {
      const { ok } = await storeDistributionsSellerToUnityWithFiles(formData);
      if (!ok) {
        throw new Error("Erro ao registrar distribuicao");
      }

      toast.success(
        `Distribuicao salva com sucesso para ${modifiedUnities.length} unidade${
          modifiedUnities.length > 1 ? "s" : ""
        }`
      );

      // Limpar ref do produto distribuido
      delete userChangesRef.current[product.id];

      // Recarregar dados silenciosamente
      await fetchPage(currentPage);
      await pageActions.update();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Erro ao criar distribuicao"
      );
    } finally {
      setDistributingProductId(null);
    }
  }

  async function handleShowDetails(productId: number, productName: string) {
    setSelectedProductDetails({ id: productId, name: productName });
    const { ok, data } = await getSellerToUnityDistributionDetails(
      String(contestData.id),
      productId
    );

    if (ok) {
      setProductDetails(data.response?.distributions || []);
      setShowDetailsModal(true);
    }
  }

  // Captura snapshot de todas as alterações quando abre o dialog
  const handleOpenDistributeAllDialog = () => {
    // Combinar dados do ref (alterações de todas as páginas) com quotaStates atual
    const quotasSnapshot: { [productId: string]: { unityId: number; quota: number }[] } = {};

    // 1. Primeiro, pega todas as alterações do ref (páginas anteriores)
    Object.entries(userChangesRef.current).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        quotasSnapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // 2. Depois, pega do quotaStates atual (página atual) - sobrescreve se já existir
    Object.entries(quotaStates).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        quotasSnapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // Criar produtos virtuais para os IDs que tem cotas definidas
    const productsWithQuotas = Object.keys(quotasSnapshot).map(productIdStr => {
      const productId = parseInt(productIdStr);
      const existingProduct = simplifiedData.find(
        p => p.contest_product_classification.id === productId
      );
      if (existingProduct) {
        return existingProduct;
      }
      // Criar um produto placeholder para produtos de outras páginas
      return {
        contest_product_classification: {
          id: productId,
          product_name: `Produto #${productId}`,
          sale_price: 0,
          quota_total_quantity: 0,
          quota_total_distributed: "0",
        },
        unity: [],
      } as SimplifiedProduct;
    });

    setDistributeAllSnapshot({
      products: productsWithQuotas,
      quotaStates: quotasSnapshot,
    });
    setShowDistributeAllDialog(true);
  };

  async function handleDistributeAll() {
    if (!sellerId || !currentUnity) {
      toast.error("Dados do vendedor ou unidade nao encontrados.");
      return;
    }

    if (distributeAllFiles.length === 0) {
      toast.error("E obrigatorio anexar um arquivo para distribuir.");
      return;
    }

    if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot.quotaStates).length === 0) {
      toast.error("Nenhum produto para distribuir.");
      return;
    }

    setIsDistributingAll(true);
    setShowDistributeAllDialog(false);

    const distributions: Array<{
      contest_product_classification_id: number;
      from_seller_id: number;
      unities: Array<{ id: number; quota_quantity: number }>;
    }> = [];
    let skippedCount = 0;
    let validationErrorCount = 0;

    // Usar o snapshot capturado quando o dialog foi aberto
    const allProductIds = Object.keys(distributeAllSnapshot.quotaStates);

    for (const productIdStr of allProductIds) {
      const productId = parseInt(productIdStr);
      const unitiesToDistribute = distributeAllSnapshot.quotaStates[productId] || [];

      if (unitiesToDistribute.length === 0) {
        skippedCount++;
        continue;
      }

      // So validar se o produto esta na pagina atual
      const productInCurrentPage = simplifiedData.find(
        (p) => p.contest_product_classification.id === productId
      );
      if (productInCurrentPage) {
        const validationInfo = getQuotaValidationInfo(productId, unitiesToDistribute);
        if (validationInfo.isOverLimit) {
          validationErrorCount++;
          continue;
        }
      }

      distributions.push({
        contest_product_classification_id: productId,
        from_seller_id: sellerId,
        unities: unitiesToDistribute.map((unity) => ({
          id: unity.unityId,
          quota_quantity: unity.quota,
        })),
      });
    }

    if (distributions.length === 0) {
      setIsDistributingAll(false);
      toast.info("Nenhuma distribuição válida para salvar.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contest_id", String(contestData.id));
      formData.append("lottery_id", String(contestData.lottery_id || contestData.lottery?.id || 0));
      formData.append("distributions", JSON.stringify(distributions));
      distributeAllFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok, data } = await distributionSellerForUnityBulkWithFiles(formData);

      if (ok) {
        const message =
          skippedCount > 0 || validationErrorCount > 0
            ? `${distributions.length} distribuições salvas com sucesso!${skippedCount > 0 ? ` ${skippedCount} produtos sem cotas ignorados.` : ""}${validationErrorCount > 0 ? ` ${validationErrorCount} produtos com erro de validação.` : ""}`
            : `Todas as ${distributions.length} distribuições foram salvas com sucesso!`;
        toast.success(message);

        // Limpar todos os states após distribuir
        setQuotaStates({});
        setDistributeAllSnapshot(null);
        userChangesRef.current = {};

        // Recarregar dados simplificados após salvar
        await fetchPage(currentPage);
        await pageActions.update();
      } else {
        toast.error(data?.message || "Erro ao salvar distribuições.");
      }
    } catch (error) {
      console.error("Erro no processo de distribuir tudo:", error);
      toast.error("Erro no processo de distribuição em lote.");
    } finally {
      setIsDistributingAll(false);
      setDistributeAllFiles([]);
      setDistributeAllSnapshot(null);
    }
  }

  if (isModalLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoaderSpin />
      </div>
    );
  }

  return (
    <>
      <div className="h-[70vh] overflow-y-auto" style={{ maxHeight: "70vh" }}>
        <div className="w-full h-auto">
          {/* Header melhorado do concurso */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: contestData?.lottery?.color || '#023859' }}
                >
                  {contestData?.lottery?.name}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    {contestData?.name}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Validade: {contestData?.validation_date
                      ? Mask.transformMaskBirthdayInBr(contestData?.validation_date)
                      : "Não informado"}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleOpenDistributeAllDialog}
                className={cn("flex gap-2", colors.bg_primary)}
                disabled={isDistributingAll || isModalLoading}
              >
                {isDistributingAll ? (
                  <>
                    Distribuindo... <LoaderSpin />
                  </>
                ) : (
                  "Distribuir Tudo"
                )}
              </Button>
            </div>
          </div>

          {/* Filtro B: */}
          <div className="mb-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Filtrar por produto (ex: B200)"
                value={productFilter}
                onChange={(e) => {
                  setProductFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            {paginationInfo && paginationInfo.total > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Mostrando {simplifiedData.length} de {paginationInfo.total} produtos
                {productFilter && ` (filtrado por "${productFilter}")`}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3 overflow-hidden" style={{ maxHeight: "calc(70vh - 200px)" }}>
            <Table className="flex flex-col">
              <TableHeader className="flex-shrink-0 sticky top-0 bg-white dark:bg-gray-950 z-10">
                <TableRow className="flex w-full">
                  <TableHead className="flex-1">Produto</TableHead>
                  <TableHead className="w-32 text-center">Saldo Disponível</TableHead>
                  <TableHead className="flex-1 text-center">Distribuição</TableHead>
                  <TableHead className="w-20 text-center">Detalhes</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody className="flex-1 overflow-y-auto flex flex-col">
                {simplifiedData.map((product: SimplifiedProduct) => {
                  const productId = product.contest_product_classification.id;
                  const localQuotas = quotaStates[productId] || [];
                  const availableBalance = getAvailableBalance(productId);

                  return (
                    <TableRow key={productId} className="flex w-full">
                      <TableCell className="align-top flex-1">
                        {product.contest_product_classification.product_name}
                      </TableCell>
                      <TableCell className="text-center align-top w-32">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-medium text-lg">
                            {availableBalance}
                          </span>
                          <span className="text-xs text-gray-500">
                            cotas disponíveis
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="align-top flex-1">
                        {currentUnity && (
                          <div className="border rounded p-2 mb-2">
                            <div className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                              <div className="flex-1 text-sm">
                                <span className="font-medium">
                                  {currentUnity.name}
                                </span>
                              </div>
                              <Input
                                type="number"
                                min="0"
                                max={availableBalance}
                                value={
                                  localQuotas.find(
                                    (q) => q.unityId === currentUnity.id
                                  )?.quota ?? 0
                                }
                                onChange={(e) =>
                                  handleQuotaChange(
                                    productId,
                                    currentUnity.id,
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-20"
                                disabled={distributingProductId === productId}
                              />
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handleCopyIndividualDistribution(
                                      currentUnity.id,
                                      localQuotas.find(
                                        (q) => q.unityId === currentUnity.id
                                      )?.quota ?? 0,
                                      currentUnity.name
                                    )
                                  }
                                  disabled={
                                    !localQuotas.find(
                                      (q) => q.unityId === currentUnity.id
                                    )?.quota
                                  }
                                  title="Copiar distribuição"
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handlePasteDistribution(
                                      productId,
                                      currentUnity.id
                                    )
                                  }
                                  disabled={!copiedDistribution}
                                  title="Colar distribuição"
                                >
                                  <Clipboard className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Validação e resumo */}
                        {(() => {
                          const validationInfo = getQuotaValidationInfo(
                            productId,
                            localQuotas
                          );
                          if (validationInfo.totalInputQuotas === 0)
                            return null;

                          return (
                            <div className="mt-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-800">
                              <div className="text-sm">
                                <span className="font-medium">
                                  Total: {validationInfo.totalInputQuotas} /{" "}
                                  {validationInfo.availableBalance}
                                </span>
                                {validationInfo.isOverLimit ? (
                                  <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                                    Excesso de{" "}
                                    {validationInfo.totalInputQuotas -
                                      validationInfo.availableBalance}{" "}
                                    cotas sobre o limite disponível
                                  </div>
                                ) : validationInfo.remaining > 0 ? (
                                  <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                                    Ainda restam {validationInfo.remaining}{" "}
                                    cotas disponíveis
                                  </div>
                                ) : (
                                  <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                                    Utilizando todo o saldo disponível
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <Button
                          variant="outline"
                          className={cn(
                            "w-full",
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                productId,
                                localQuotas
                              );
                              return validationInfo.isOverLimit
                                ? "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:border-red-700 dark:text-red-300"
                                : colors.bg_primary;
                            })()
                          )}
                          disabled={
                            distributingProductId === productId ||
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                productId,
                                localQuotas
                              );
                              const hasValidChanges = localQuotas.some(
                                (quota) => quota.quota > 0
                              );
                              return (
                                validationInfo.isOverLimit || !hasValidChanges
                              );
                            })()
                          }
                          onClick={() => setConfirmDialogProduct(product.contest_product_classification)}
                        >
                          {distributingProductId === productId ? (
                            <LoaderSpin />
                          ) : (
                            (() => {
                              const validationInfo = getQuotaValidationInfo(
                                productId,
                                localQuotas
                              );
                              const hasValidChanges = localQuotas.some(
                                (quota) => quota.quota > 0
                              );

                              if (validationInfo.isOverLimit) {
                                return "Distribuicao bloqueada (excede limite)";
                              }
                              if (!hasValidChanges) {
                                return "Defina cotas para distribuir";
                              }
                              return "Fazer distribuicao";
                            })()
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center align-top w-20">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            handleShowDetails(
                              productId,
                              product.contest_product_classification.product_name
                            )
                          }
                          className="h-8 w-8"
                          title="Ver detalhes da distribuicao"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {simplifiedData.length < 1 && (
                  <TableRow className="flex w-full">
                    <TableCell colSpan={4} className="flex-1 text-center">
                      Nenhum produto encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginacao */}
          {paginationInfo && paginationInfo.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Pagina {paginationInfo.current_page} de {paginationInfo.last_page} ({paginationInfo.total} produtos)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    fetchPage(newPage);
                  }}
                  disabled={currentPage <= 1}
                >
                  Anterior
                </Button>
                <span className="text-sm px-2">
                  {currentPage} / {paginationInfo?.last_page || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    fetchPage(newPage);
                  }}
                  disabled={currentPage >= (paginationInfo?.last_page || 1)}
                >
                  Proximo
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de confirmação individual */}
      <Dialog
        open={!!confirmDialogProduct}
        onOpenChange={(open) => !open && setConfirmDialogProduct(null)}
      >
        <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Distribuição</DialogTitle>
            <DialogDescription>
              {confirmDialogProduct &&
                (() => {
                  const confirmLocalQuotas =
                    quotaStates[confirmDialogProduct.id] || [];
                  const confirmTotalQuotas = confirmLocalQuotas.reduce(
                    (sum, item) => sum + item.quota,
                    0
                  );
                  return (
                    <div className="space-y-2">
                      <p>
                        Você está prestes a distribuir as cotas para o produto:
                      </p>
                      <p className="font-semibold">
                        {confirmDialogProduct.product_name} -{" "}
                        {confirmDialogProduct.product_classification?.name}
                      </p>
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                        <p className="text-sm mb-2">
                          <strong>Distribuição para unidade:</strong>
                        </p>
                        <div className="space-y-1 mb-3">
                          {confirmLocalQuotas.map((quota) => {
                            if (quota.quota > 0) {
                              return (
                                <div key={quota.unityId} className="text-sm">
                                  <span className="font-medium">
                                    {currentUnity?.name}
                                  </span>{" "}
                                  -{" "}
                                  <span className="text-blue-600 dark:text-blue-400">
                                    {quota.quota} cotas
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <div className="border-t pt-2">
                          <p className="text-sm">
                            <strong>Total de cotas a distribuir:</strong>{" "}
                            {confirmTotalQuotas}
                          </p>
                          <p className="text-sm">
                            <strong>Máximo disponível:</strong>{" "}
                            {getAvailableBalance(confirmDialogProduct.id)}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Esta ação não pode ser desfeita. Deseja continuar?
                      </p>
                    </div>
                  );
                })()}
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            files={confirmDialogFiles}
            onFilesChange={setConfirmDialogFiles}
            inputId="file-upload-confirm-seller-unity-simplified"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogProduct(null);
                setConfirmDialogFiles([]);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (confirmDialogProduct) {
                  handleSubmitDistribution(confirmDialogProduct, confirmDialogFiles);
                  setConfirmDialogProduct(null);
                  setConfirmDialogFiles([]);
                }
              }}
              className={cn(colors.bg_primary)}
              disabled={confirmDialogFiles.length === 0}
            >
              Confirmar Distribuição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de detalhes da distribuição */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>
              Detalhes da Distribuição - {selectedProductDetails?.name}
            </DialogTitle>
            <DialogDescription>
              Histórico completo das distribuições para este produto
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {productDetails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unidade</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Quantidade</TableHead>
                    <TableHead>Data de criação</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado por</TableHead>
                    <TableHead>Data de Atualização</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productDetails.map((detail: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        {detail.unity?.name || `Unidade ${detail.unity_id}`}
                      </TableCell>
                      <TableCell>
                        {detail.seller?.name ||
                          detail.from_seller?.name ||
                          `Vendedor ${detail.from_seller_id}`}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {detail.quota_quantity}
                        </span>{" "}
                        cotas
                      </TableCell>
                      <TableCell>
                      {detail.created_at
                          ? new Date(
                              new Date(detail.created_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            detail.status?.id == 1
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                              : detail.status?.id == 2
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                          }`}
                        >
                          {detail.status?.id == 1
                            ? "Pendente"
                            : detail.status?.id == 2
                            ? "Aceito"
                            : "Rejeitado"}
                        </span>
                      </TableCell>
                      <TableCell>{detail.created_by?.name || "N/A"}</TableCell>
                      <TableCell>
                      {detail.processed_at
                          ? new Date(
                              new Date(detail.processed_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-center">
                        {detail.files && detail.files.length > 0 ? (
                          detail.files.length === 1 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => window.open(detail.files[0].file_url, "_blank")}
                              title={detail.files[0].file_name || "Baixar arquivo"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title={`${detail.files.length} arquivos`}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {detail.files.map((file: any) => (
                                  <DropdownMenuItem
                                    key={file.id}
                                    onClick={() => window.open(file.file_url, "_blank")}
                                    className="cursor-pointer"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    {file.file_name || `Arquivo ${file.id}`}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nenhuma distribuição encontrada para este produto
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailsModal(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmacao para Distribuir Tudo */}
      <Dialog open={showDistributeAllDialog} onOpenChange={(open) => {
        if (!open) {
          setShowDistributeAllDialog(false);
          setDistributeAllSnapshot(null);
        }
      }}>
        <DialogContent className="z-[60] max-w-2xl" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Distribuicao Geral</DialogTitle>
            <DialogDescription>
              {(() => {
                const snapshotData = distributeAllSnapshot || { products: [], quotaStates: {} };
                const productsWithQuotas = snapshotData.products.filter(
                  (product: SimplifiedProduct) => {
                    const productId = product.contest_product_classification.id;
                    const localQuotas = snapshotData.quotaStates[productId] || [];
                    return localQuotas.some((quota: { unityId: number; quota: number }) => quota.quota > 0);
                  }
                );

                let grandTotal = 0;

                return (
                  <div className="space-y-3">
                    <p>
                      Voce esta prestes a distribuir todos os produtos com cotas definidas para:
                    </p>
                    <p className="font-semibold text-blue-600 dark:text-blue-400">
                      {currentUnity?.name}
                    </p>
                    {paginationInfo && paginationInfo.total > paginationInfo.per_page && (
                      <p className="text-xs text-orange-600 dark:text-orange-400">
                        Inclui dados de todas as {paginationInfo.last_page} paginas ({snapshotData.products.length} produtos com cotas definidas em todas as paginas)
                      </p>
                    )}

                    {productsWithQuotas.length > 0 ? (
                      <>
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded max-h-64 overflow-y-auto">
                          <p className="text-sm font-medium mb-3">
                            Resumo da distribuicao ({productsWithQuotas.length} produto{productsWithQuotas.length > 1 ? "s" : ""}):
                          </p>
                          <div className="space-y-3">
                            {productsWithQuotas.map((product: SimplifiedProduct) => {
                              const productId = product.contest_product_classification.id;
                              const localQuotas = snapshotData.quotaStates[productId] || [];
                              const quotasWithValues = localQuotas.filter((q: { unityId: number; quota: number }) => q.quota > 0);
                              const productTotal = quotasWithValues.reduce((sum: number, q: { unityId: number; quota: number }) => sum + q.quota, 0);
                              grandTotal += productTotal;

                              return (
                                <div key={productId} className="border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0">
                                  <div className="flex justify-between items-center">
                                    <span className="font-medium text-sm">
                                      {product.contest_product_classification.product_name}
                                    </span>
                                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                                      {productTotal} cotas
                                    </span>
                                  </div>
                                  <div className="pl-3 mt-1">
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      - {currentUnity?.name}: {productTotal} cotas
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="border-t border-gray-300 dark:border-gray-600 mt-3 pt-3">
                            <div className="flex justify-between font-semibold">
                              <span>Total Geral:</span>
                              <span className="text-green-600 dark:text-green-400">{grandTotal} cotas</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Esta acao ira processar todos os produtos acima. Produtos
                          sem cotas definidas ou que excedam o limite serao
                          ignorados.
                        </p>
                      </>
                    ) : (
                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          Nenhum produto tem cotas definidas para distribuicao.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            files={distributeAllFiles}
            onFilesChange={setDistributeAllFiles}
            inputId="file-upload-distribute-all-seller-unity-simplified"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDistributeAllDialog(false);
                setDistributeAllFiles([]);
              }}
              disabled={isDistributingAll}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDistributeAll}
              className={cn(colors.bg_primary)}
              disabled={isDistributingAll || distributeAllFiles.length === 0}
            >
              {isDistributingAll ? (
                <>
                  <LoaderSpin />
                  Distribuindo...
                </>
              ) : (
                "Confirmar Distribuição Geral"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
